import { computed, linkedSignal, Signal, signal } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ValidationStatus, ValidationService } from './validation.service';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SidenavValidationRegistry } from './sidenav-validation-registry.service';
import { SectionNames } from './section-names.model';
import { AssetMetadata, CompassRoute } from '@r1-compass-library/models';

function areSectionListsEqual<SectionName extends string>(left: SectionName[], right: SectionName[]): boolean {
    return left.length === right.length && left.every((section, index) => section === right[index]);
}

/**
 * Abstract base class for feature validation state services.
 *
 * Tracks which sections in a multi-section feature are currently invalid and exposes
 * reactive signals that components and the sidenav can consume to display validation indicators.
 *
 * ---
 *
 * ### How to implement
 *
 * **1. Import the sections constant from your feature's validation service:**
 * ```typescript
 * import { CONTENT_LIBRARY_SECTIONS, ContentLibrarySectionName } from './content-library-validation.service';
 * ```
 *
 * 2. Extend this class, provide the generic, and implement `sectionState`
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class ContentLibraryeValidationStateService extends ValidationStateService<ContentLibrarySectionName> {
 *     protected override readonly sectionStateRecord = signal(
 *         Object.fromEntries(CONTENT_LIBRARY_SECTIONS.map(p => [p, false])) as Record<ContentLibrarySectionName, boolean>
 *     ).asReadonly();
 * }
 * ```
 *
 * **3. Register with `SidenavValidationRegistry` in your feature's parent component** so the sidenav
 *    automatically reflects validation state {@link SidenavValidationRegistry}
 *
 * **4. Call `setSectionsFromStatus` or `setSectionInvalid`** from your section components after
 *    calling `validateSection` or `validateModel` on the feature's {@link ValidationService}.
 *
 * @template SectionName - Union of string literal section names for the feature (e.g. `'definition' | 'rules'`).
 *
 */
export abstract class ValidationStateService<SectionName extends SectionNames> {
    /**
     * The live, mutable signal holding the validity state of each section.
     * Implemented by each concrete subclass using the feature's section names.
     *
     * Initialize all sections to `false` (valid):
     * ```typescript
     * protected override readonly sectionStateRecord = signal(
     *     Object.fromEntries(MY_FEATURE_SECTIONS.map(p => [p, false])) as Record<MyFeatureSectionName, boolean>
     * );
     * ```
     */
    protected readonly sectionStateRecord: Signal<Record<SectionName, boolean>>;

    /** Read-only snapshot of the full section validity map. Useful for components that need the raw record. */
    private readonly sectionState = linkedSignal<Record<SectionName, boolean>>(() => this.sectionStateRecord());

    private readonly initiallyInvalid: boolean;

    /** Tracks whether initial validation data has loaded */
    readonly #isLoaded = signal<boolean>(false);
    public readonly isLoaded = this.#isLoaded.asReadonly();

    protected constructor(routes: readonly CompassRoute[], initiallyInvalid = true) {
        const stateEntries = routes
            .filter(route => route.path !== '' && !route.excludeFromValidation)
            .map(route => [route.sectionKey ?? route.path, initiallyInvalid]);

        const initialState = Object.fromEntries(stateEntries) as Record<SectionName, boolean>;

        this.sectionStateRecord = signal(initialState).asReadonly();
        this.initiallyInvalid = initiallyInvalid;
    }

    /** Derived signal containing only the names of currently invalid sections. Consumed by the sidenav. */
    public readonly invalidSections = computed<SectionName[]>(
        () => {
            if (!this.#isLoaded()) {
                return [];
            }
            return (Object.entries(this.sectionState()) as [SectionName, boolean][])
                .filter(([, isInvalid]) => isInvalid)
                .map(([name]) => name);
        },
        { equal: areSectionListsEqual }
    );

    public shouldDisableSave(
        entity: AssetMetadata | undefined,
        canDeactivate: {
            dirtied: boolean;
            valid: boolean;
        },
        invalidSections: SectionNames[],
        sectionName: SectionNames,
        playLogicException?: boolean
    ): boolean {
        if (playLogicException !== undefined) {
            // The play has the same interface for this service,
            //     but its own logic a bit different than other entities.
            // - Definition needs a name
            // - Importer/Builder record selection does not need to be blocked
            // - Talking Points are only blocked while typing in the Froala box
            // Each play handles it with their own logic passed here. Even if it looks like a dud
            //    like the importer/builder.
            return playLogicException;
        }
        if (!entity) {
            return !canDeactivate.valid;
        } else {
            return (
                entity.enabled === true &&
                (entity.valid === false || canDeactivate.valid === false || invalidSections.includes(sectionName))
            );
        }
    }

    /**
     * Marks a single section as valid or invalid.
     * Use this when only one section's state changes (e.g. a targeted reactive subscription).
     *
     * @param section   - The section name to update.
     * @param isInvalid - `true` if the section has validation errors, `false` if it is valid.
     */
    public setSectionInvalid(section: SectionName, isInvalid: boolean): void {
        this.sectionState.update(sect => ({ ...sect, [section]: isInvalid }));
    }

    /**
     * Bulk-updates all section states from a `ValidationStatus` returned by a `ValidationService`.
     * Any section present in `status.erroredSections` is marked invalid; all others are marked valid.
     *
     * Typically called after `validateModel` (on load) or `validateSection` (on save).
     *
     * @param status - The result of a `validateModel` or `validateSection` call.
     */
    public setSectionsFromStatus(status: ValidationStatus<SectionName>): void {
        this.sectionState.update(
            section =>
                Object.fromEntries(
                    Object.keys(section).map(key => [key, status.erroredSections.has(key as SectionName)])
                ) as Record<SectionName, boolean>
        );
        this.#isLoaded.set(true);
    }

    /**
     * @description If an entity (eg a Play) has invalid sections and then a new one is created,
     * then without a reset of the status of the sections, the other sections of the new play will
     * still be marked. This can be called when an enity initializes any given singular
     * existing or new entity view.
     * @param isNew - Set to true when creating a new entity, false when loading an existing one.
     */
    public resetValidityOfSections(isNew = false) {
        this.#isLoaded.set(isNew);
        this.sectionState.update(
            section =>
                Object.fromEntries(Object.keys(section).map(key => [key, this.initiallyInvalid])) as Record<
                    SectionName,
                    boolean
                >
        );
    }
}
